import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Plus, X, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, inr, fmtDate } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";

const EMPTY = { name: "", email: "", mobile: "", contact_person: "", address: "", city: "", state: "", pincode: "", notes: "" };
const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-700",
  kyc_submitted: "bg-blue-100 text-blue-700", kyc_approved: "bg-blue-100 text-blue-700",
  rejected: "bg-red-100 text-red-600", suspended: "bg-red-100 text-red-600", blocked: "bg-red-100 text-red-600",
};

export default function AdminFranchises() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () => api.get("/admin/franchises").then(({ data }) => setRows(data)).catch((e) => { toast.error(fmtErr(e)); setRows([]); });
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/franchises", form);
      setCreated(data);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <FullScreenLoader label="Loading franchises" />;

  return (
    <div className="space-y-5" data-testid="admin-franchises-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-shipany-navy">Franchise Network</h1>
          <p className="text-sm text-slate-500">Franchise accounts are created here only — no public sign-up.</p>
        </div>
        <button onClick={() => { setCreated(null); setOpen(true); }} data-testid="create-franchise-btn"
          className="flex items-center gap-2 rounded-xl bg-shipany-blue px-4 py-2.5 text-sm font-bold text-white transition-all hover:shadow-lg">
          <Plus className="h-4 w-4" /> Create Franchise
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs font-bold uppercase tracking-wide text-slate-400">
              <th className="px-5 py-3">Franchise</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Location</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3">KYC</th><th className="px-5 py-3 text-right">Customers</th>
              <th className="px-5 py-3 text-right">Commissions</th><th className="px-5 py-3 text-right">Wallet</th><th className="px-5 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.franchise_id} onClick={() => navigate(`/admin/franchises/${f.franchise_id}`)} data-testid={`franchise-row-${f.franchise_id}`}
                className="cursor-pointer border-b border-slate-50 transition-colors hover:bg-shipany-blue/[0.03]">
                <td className="px-5 py-3.5"><p className="font-semibold text-shipany-navy">{f.name}</p><p className="mono text-[10px] text-slate-400">{f.franchise_code}</p></td>
                <td className="px-5 py-3.5"><p className="text-xs text-slate-600">{f.email}</p><p className="text-xs text-slate-400">{f.mobile}</p></td>
                <td className="px-5 py-3.5 text-xs text-slate-600">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-5 py-3.5"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${STATUS_BADGE[f.status] || "bg-slate-100 text-slate-500"}`} data-testid={`franchise-status-${f.franchise_id}`}>{f.status}</span></td>
                <td className="px-5 py-3.5"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${f.kyc_status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{f.kyc_status}</span></td>
                <td className="mono px-5 py-3.5 text-right text-xs font-semibold">{f.customer_count ?? 0}</td>
                <td className="mono px-5 py-3.5 text-right text-xs font-semibold text-emerald-600">{inr(f.earnings || 0)}</td>
                <td className="mono px-5 py-3.5 text-right text-xs font-semibold">{inr(f.wallet?.available || 0)}</td>
                <td className="px-5 py-3.5 text-xs text-slate-500">{fmtDate(f.created_at)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-slate-400" data-testid="franchises-empty"><Store className="mx-auto mb-2 h-6 w-6 text-slate-300" />No franchises yet — create the first partner account.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-franchise-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Create Franchise Account</h2>
              <button onClick={() => setOpen(false)} data-testid="create-franchise-close" className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            {created ? (
              <div className="mt-5 space-y-4" data-testid="franchise-created-box">
                <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
                  <div className="text-sm text-emerald-800">
                    <p className="font-bold">Franchise created — {created.franchise_code}</p>
                    <p className="mt-1 text-xs">Share these credentials. The franchise completes KYC, then you activate the account from the detail page.</p>
                  </div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4 text-sm">
                  <p><span className="text-slate-500">Login:</span> <span className="mono font-bold">shipany.in/franchise-login</span></p>
                  <p className="mt-2"><span className="text-slate-500">Email:</span> <span className="mono font-bold text-shipany-navy" data-testid="created-franchise-email">{created.email}</span></p>
                  <p className="mt-2 flex items-center gap-2"><span className="text-slate-500">Temporary password:</span>
                    <span className="mono font-bold text-shipany-navy" data-testid="created-franchise-temp-password">{created.temporary_password}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(created.temporary_password); toast.success("Copied"); }} data-testid="copy-franchise-temp-password" className="text-shipany-blue"><Copy className="h-4 w-4" /></button>
                  </p>
                </div>
                <button onClick={() => setOpen(false)} data-testid="franchise-created-done-btn" className="w-full rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white">Done</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Franchise / firm name *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="franchise-name-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Contact person</label><input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="franchise-contact-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Email *</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="franchise-email-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Mobile (10-digit) *</label><input required maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} data-testid="franchise-mobile-input" className={inputCls} /></div>
                <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold text-slate-500">Address</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="franchise-address-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">City</label><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="franchise-city-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">State</label><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="franchise-state-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Pincode</label><input maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} data-testid="franchise-pincode-input" className={inputCls} /></div>
                <div><label className="mb-1 block text-xs font-semibold text-slate-500">Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="franchise-notes-input" className={inputCls} /></div>
                <button disabled={busy} data-testid="franchise-submit-btn" className="rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white disabled:opacity-60 sm:col-span-2">
                  {busy ? "Creating…" : "Create Franchise"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
